<!-- == Unichat Extension ==
     Página de gerenciamento de Setores (PREFEITURA → SECRETARIAS → DEPARTAMENTOS)
     Localização: Configurações > Setores
-->
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SectorModal from './SectorModal.vue';
import SectorTeamsModal from './SectorTeamsModal.vue';
import {
  getSectors,
  deleteSector,
} from 'dashboard/unichat/api/unichat.js';

const store = useStore();
const { isAdmin } = useAdmin();

// Estado
const sectors = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');
const showModal = ref(false);
const editingSector = ref(null);
const showTeamsModal = ref(false);
const managingTeamsSector = ref(null);

// Computed
const accountId = computed(() => store.getters.getCurrentAccountId);

const filteredSectors = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return sectors.value;
  return sectors.value.filter(
    s =>
      s.name?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q)
  );
});

// Métodos
const fetchSectors = async () => {
  isLoading.value = true;
  try {
    const { data } = await getSectors(accountId.value);
    sectors.value = data || [];
  } catch {
    useAlert('Erro ao carregar secretarias.');
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => {
  editingSector.value = null;
  showModal.value = true;
};

const openEdit = sector => {
  editingSector.value = { ...sector };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingSector.value = null;
};

const onSaved = () => {
  closeModal();
  fetchSectors();
};

const openTeamsModal = sector => {
  managingTeamsSector.value = sector;
  showTeamsModal.value = true;
};

const closeTeamsModal = () => {
  showTeamsModal.value = false;
  managingTeamsSector.value = null;
};

const onDelete = async sector => {
  if (!confirm(`Excluir a secretaria "${sector.name}"?`)) return;
  try {
    await deleteSector(accountId.value, sector.id);
    useAlert('Secretaria excluída com sucesso.');
    fetchSectors();
  } catch {
    useAlert('Erro ao excluir secretaria.');
  }
};

onMounted(() => {
  fetchSectors();
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    loading-message="Carregando secretarias..."
    :no-records-found="!isLoading && !sectors.length"
    no-records-message="Nenhuma secretaria cadastrada ainda."
  >
    <!-- Cabeçalho -->
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        title="Secretarias"
        description="Organize o atendimento por secretarias da prefeitura. Cada secretaria agrupa setores e usuários."
        search-placeholder="Buscar secretaria..."
      >
        <template v-if="sectors.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ sectors.length }} secretaria{{ sectors.length !== 1 ? 's' : '' }}
          </span>
        </template>
        <template #actions>
          <Button
            v-if="isAdmin"
            label="Nova Secretaria"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <!-- Lista -->
    <template #body>
      <span
        v-if="!filteredSectors.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        Nenhuma secretaria encontrada para "{{ searchQuery }}".
      </span>

      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="sector in filteredSectors"
          :key="sector.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <!-- Ícone + Info -->
          <div class="flex items-start gap-4">
            <div
              class="flex items-center flex-shrink-0 size-10 justify-center rounded-xl outline outline-1 outline-n-weak -outline-offset-1"
            >
              <Icon icon="i-lucide-building-2" class="size-4 text-n-slate-11" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ sector.name }}
              </span>
              <p v-if="sector.description" class="mb-0 text-n-slate-11 text-body-main">
                {{ sector.description }}
              </p>
              <span
                v-if="sector.active === false"
                class="text-xs text-n-ruby-11 font-medium"
              >
                Inativo
              </span>
            </div>
          </div>

          <!-- Ações -->
          <div v-if="isAdmin" class="flex justify-end gap-3">
            <Button
              v-tooltip.top="'Gerenciar Setores vinculados'"
              icon="i-lucide-users"
              slate
              sm
              @click="openTeamsModal(sector)"
            />
            <Button
              v-tooltip.top="'Editar secretaria'"
              icon="i-woot-settings"
              slate
              sm
              @click="openEdit(sector)"
            />
            <Button
              v-tooltip.top="'Excluir secretaria'"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="onDelete(sector)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <!-- Modal criar/editar -->
  <SectorModal
    v-if="showModal"
    :sector="editingSector"
    :account-id="accountId"
    @close="closeModal"
    @saved="onSaved"
  />

  <!-- Modal gerenciar setores vinculados -->
  <SectorTeamsModal
    v-if="showTeamsModal && managingTeamsSector"
    :sector="managingTeamsSector"
    :account-id="accountId"
    @close="closeTeamsModal"
  />
</template>
